import { PropsWithChildren, useMemo } from 'react';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { useChatRuntime } from '@assistant-ui/react-ai-sdk';
import * as R from 'ramda';

import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import {
  ATOM_CLIENT_TIMEZONE_HEADER,
  ATOM_DEVICE_HEADER,
  ATOM_IMPERSONATE_USER_HEADER,
  ATOM_LLM_MODEL_HEADER,
  ATOM_LLM_OVERRIDES_HEADER,
  DEFAULT_HEADERS,
} from '@atom/utilities/requestUtilities';

import { useChatContext } from './ChatContext';
import ChatResumeAwareTransport from './ChatResumeAwareTransport';

const ChatRuntime = ({ children }: PropsWithChildren<{}>) => {
  const [, { updateSearchParams }] = useNavigation({
    isNewThread: SearchParamType.BOOLEAN,
  });

  const {
    threadId,
    threadsData: { createThread },
    messagesData: { messages },
    attachmentsData: {
      getStagedAttachments,
      clearStagedAttachments,
      addAttachment,
    },
    impersonationData: { getImpersonatedUserId },
    modelSelectionData: { getModel, getScope },
    resume,
    setResume,
    onError,
  } = useChatContext();

  const transport = useMemo(
    () =>
      new ChatResumeAwareTransport({
        api: '/api/v1/chat',
        getResumeToolName: () => resume.current?.toolName,
        prepareSendMessagesRequest: async options => {
          // reset error on each new user-sent message
          onError(null);

          const userMessages = options.messages.filter(
            message => message.role === 'user',
          );

          // create thread on first user message
          // doing this manually allows us to initialize workingMemory on thread creation
          if (userMessages.length === 1) {
            // optimistically create the thread in state
            // mastra will handle creating the thread via the message if it does not exist
            const optimistic = true;
            await createThread(threadId, optimistic);
            // strip query param after thread creation
            updateSearchParams({ isNewThread: undefined });
          }

          const stagedAttachments = getStagedAttachments();
          const impersonatedUserId = getImpersonatedUserId();
          const model = getModel();
          const scope = getScope();
          const overrides = model && scope === 'all' ? 'true' : undefined;

          const attachments = stagedAttachments.map(attachment => ({
            mediaId: attachment.mediaId,
            mimeType: attachment.mimeType,
            fileId: attachment.fileId,
          }));

          if (attachments.length > 0) {
            const lastUserMessage = R.last(userMessages);

            // Defer state updates to the next tick so they don't run while
            // assistant-ui is mid-send. Updating state synchronously here
            // would happen during another component's render/dispatch and
            // could clear stagedAttachments before the request is dispatched.
            setTimeout(() => {
              if (lastUserMessage?.id) {
                addAttachment(lastUserMessage.id, stagedAttachments);
              }
              clearStagedAttachments();
            }, 0);
          }

          const message = R.last(userMessages);

          const typedText =
            (message as any)?.parts?.find((part: any) => part.type === 'text')
              ?.text ?? '';

          const currentResume = resume.current;
          const resumeData = currentResume
            ? {
                ...currentResume.resumeData,
                message: currentResume.resumeData.message || typedText,
              }
            : undefined;

          // set timeout to ensure we don't clear the pending resume until the next tick after request fires
          if (currentResume) {
            setTimeout(() => setResume(null), 0);
          }

          return {
            ...options,
            body: {
              ...options.body,
              // only send the most recent user message
              // the back end will retrieve the message history
              messages: R.last(userMessages),
              threadId,
              attachments: attachments.length > 0 ? attachments : undefined,
              runId: currentResume?.runId,
              resumeData,
            },
            headers: R.reject(R.isNil, {
              [ATOM_DEVICE_HEADER]: DEFAULT_HEADERS[ATOM_DEVICE_HEADER],
              [ATOM_CLIENT_TIMEZONE_HEADER]:
                DEFAULT_HEADERS[ATOM_CLIENT_TIMEZONE_HEADER],
              [ATOM_IMPERSONATE_USER_HEADER]: impersonatedUserId || undefined,
              [ATOM_LLM_MODEL_HEADER]: model || undefined,
              [ATOM_LLM_OVERRIDES_HEADER]: overrides || undefined,
            }),
          };
        },
      }),
    [
      threadId,
      createThread,
      getStagedAttachments,
      clearStagedAttachments,
      addAttachment,
      getImpersonatedUserId,
      getModel,
      getScope,
      setResume,
    ],
  );

  const runtime = useChatRuntime({
    transport,
    // @ts-ignore
    messages,
    onError,
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
};

export default ChatRuntime;
